import React, { useEffect, useState, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  ArrowLeft, Upload, Trash2, Image, Video, Plus, X, Play, 
  Eye, EyeOff, CheckSquare, Square, RefreshCw, Search,
  ChevronLeft, ChevronRight, Archive, MoreVertical, FolderPlus, Folder
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminGalleries = () => {
  const { t, i18n } = useTranslation();
  const { isAdmin, loading: authLoading } = useAdmin();
  const [galleries, setGalleries] = useState([]); // Combined event + custom galleries
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newGalleryName, setNewGalleryName] = useState('');
  const [newGalleryDescription, setNewGalleryDescription] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'events', 'custom'

  const adminPassword = localStorage.getItem('adminPassword') || '';

  // Fetch all galleries (events + custom)
  const fetchGalleries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/galleries/all`);
      setGalleries(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching galleries:', err);
      // Fallback to events endpoint
      try {
        const fallback = await axios.get(`${API}/events/with-gallery-stats`);
        setGalleries(fallback.data.map(e => ({
          id: e.id,
          name: e.title_it || e.title_en || 'Evento',
          type: 'event',
          gallery_photos: e.gallery_photos || 0,
          gallery_videos: e.gallery_videos || 0,
          gallery_visible: e.gallery_visible !== false,
          date_start: e.date_start,
          cover_image: e.cover_image
        })));
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
      setLoading(false);
    }
  }, []);

  const fetchGalleryItems = useCallback(async (galleryId) => {
    if (!galleryId) return;
    try {
      const response = await axios.get(`${API}/gallery?event_id=${galleryId}`);
      const items = response.data;
      setPhotos(items.filter(i => i.media_type !== 'video'));
      setVideos(items.filter(i => i.media_type === 'video'));
      setSelectedItems(new Set());
      setSelectMode(false);
    } catch (err) {
      console.error('Error fetching gallery:', err);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (isAdmin) {
      fetchGalleries();
    }
  }, [isAdmin, authLoading, fetchGalleries]);

  useEffect(() => {
    if (selectedGallery) {
      fetchGalleryItems(selectedGallery.id);
    } else {
      setPhotos([]);
      setVideos([]);
      setSelectedItems(new Set());
    }
  }, [selectedGallery, fetchGalleryItems]);

  // Create custom gallery
  const handleCreateCustomGallery = async (e) => {
    e.preventDefault();
    if (!newGalleryName.trim()) return;

    try {
      await axios.post(`${API}/custom-galleries`, {
        name: newGalleryName.trim(),
        description: newGalleryDescription.trim() || null
      }, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      setNewGalleryName('');
      setNewGalleryDescription('');
      setShowCreateModal(false);
      fetchGalleries();
    } catch (err) {
      console.error('Create gallery error:', err);
      setError(err.response?.data?.detail || 'Errore durante la creazione della galleria');
    }
  };

  // Delete custom gallery
  const handleDeleteCustomGallery = async (gallery) => {
    if (!window.confirm(`Sei sicuro di voler eliminare la galleria "${gallery.name}" e tutte le sue foto?`)) return;
    
    try {
      await axios.delete(`${API}/custom-galleries/${gallery.custom_id}`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      if (selectedGallery?.id === gallery.id) {
        setSelectedGallery(null);
      }
      fetchGalleries();
    } catch (err) {
      console.error('Delete gallery error:', err);
      setError('Errore durante l\'eliminazione della galleria');
    }
  };

  // File upload handler
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !selectedGallery) return;

    setUploading(true);
    setProgress({ done: 0, total: files.length });
    setError('');

    const batchSize = 5;
    let uploaded = 0;

    for (let i = 0; i < files.length; i += batchSize) {
      const batch = files.slice(i, i + batchSize);
      const formData = new FormData();
      batch.forEach(file => formData.append('files', file));
      formData.append('event_id', selectedGallery.id);
      formData.append('event_title', selectedGallery.name);

      try {
        await axios.post(`${API}/gallery/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-Admin-Password': adminPassword,
          },
        });
        uploaded += batch.length;
        setProgress({ done: uploaded, total: files.length });
      } catch (err) {
        console.error('Upload error:', err);
        setError('Errore durante il caricamento di alcuni file.');
      }
    }

    setUploading(false);
    e.target.value = '';
    fetchGalleryItems(selectedGallery.id);
    fetchGalleries(); // Refresh stats
  };

  // Add video handler
  const handleAddVideo = async (e) => {
    e.preventDefault();
    if (!videoUrl || !selectedGallery) return;

    try {
      const formData = new FormData();
      formData.append('event_id', selectedGallery.id);
      formData.append('event_title', selectedGallery.name);
      formData.append('video_url', videoUrl);
      if (videoTitle) formData.append('title', videoTitle);

      await axios.post(`${API}/gallery/video`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Admin-Password': adminPassword,
        },
      });

      setVideoUrl('');
      setVideoTitle('');
      setShowVideoForm(false);
      fetchGalleryItems(selectedGallery.id);
      fetchGalleries();
    } catch (err) {
      console.error('Video add error:', err);
      setError(err.response?.data?.detail || 'Errore durante l\'aggiunta del video');
    }
  };

  // Delete single item
  const handleDelete = async (itemId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo elemento?')) return;

    try {
      await axios.delete(`${API}/gallery/${itemId}`, {
        headers: { 'X-Admin-Password': adminPassword },
      });
      fetchGalleryItems(selectedGallery.id);
      fetchGalleries();
    } catch (err) {
      console.error('Delete error:', err);
      setError('Errore durante l\'eliminazione.');
    }
  };

  // Delete multiple items
  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;
    if (!window.confirm(`Sei sicuro di voler eliminare ${selectedItems.size} elementi?`)) return;

    try {
      await axios.post(`${API}/gallery/delete-multiple`, 
        Array.from(selectedItems),
        { headers: { 'X-Admin-Password': adminPassword } }
      );
      fetchGalleryItems(selectedGallery.id);
      fetchGalleries();
      setSelectMode(false);
    } catch (err) {
      console.error('Bulk delete error:', err);
      setError('Errore durante l\'eliminazione multipla.');
    }
  };

  // Toggle gallery visibility
  const handleToggleVisibility = async (gallery) => {
    const newVisibility = !gallery.gallery_visible;
    try {
      if (gallery.type === 'custom') {
        await axios.put(`${API}/custom-galleries/${gallery.custom_id}/visibility?visible=${newVisibility}`, {}, {
          headers: { 'X-Admin-Password': adminPassword }
        });
      } else {
        await axios.put(`${API}/events/${gallery.id}/gallery-visibility?visible=${newVisibility}`, {}, {
          headers: { 'X-Admin-Password': adminPassword }
        });
      }
      fetchGalleries();
      if (selectedGallery?.id === gallery.id) {
        setSelectedGallery({...selectedGallery, gallery_visible: newVisibility});
      }
    } catch (err) {
      console.error('Toggle visibility error:', err);
      setError('Errore nel cambio visibilità.');
    }
  };

  // Toggle item selection
  const toggleItemSelection = (itemId) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(itemId)) {
      newSelection.delete(itemId);
    } else {
      newSelection.add(itemId);
    }
    setSelectedItems(newSelection);
  };

  // Select all photos
  const selectAllPhotos = () => {
    if (selectedItems.size === photos.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(photos.map(p => p.id)));
    }
  };

  // Get photo URL
  const getPhotoUrl = (photo) => {
    if (!photo?.image_url) return '';
    return photo.image_url.startsWith('http') ? photo.image_url : `${BACKEND_URL}${photo.image_url}`;
  };

  // Filter galleries by search and type
  const filteredGalleries = galleries.filter(gallery => {
    const matchesSearch = gallery.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const isVisible = gallery.gallery_visible !== false;
    const matchesType = filterType === 'all' || 
                        (filterType === 'events' && gallery.type === 'event') ||
                        (filterType === 'custom' && gallery.type === 'custom');
    
    if (showArchived) {
      return matchesSearch && !isVisible && matchesType;
    }
    return matchesSearch && matchesType;
  });

  // Auth loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-600 to-purple-600 text-white py-6 shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">{i18n.language === 'it' ? 'Gallerie' : 'Galleries'}</h1>
              <p className="text-pink-100 mt-1">{i18n.language === 'it' ? 'Gestisci foto e video per eventi e gallerie personalizzate' : 'Manage photos and videos for events and custom galleries'}</p>
            </div>
            {/* Create Custom Gallery Button */}
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center bg-white text-pink-600 px-4 py-2 rounded-lg font-semibold hover:bg-pink-50 transition-colors"
            >
              <FolderPlus className="mr-2" size={20} />
              {i18n.language === 'it' ? 'Nuova Galleria' : 'New Gallery'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Navigation */}
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/admin/dashboard"
            className="flex items-center text-blue-600 hover:text-blue-800 font-semibold"
          >
            <ArrowLeft className="mr-2" size={20} />
            Dashboard
          </Link>
          <button
            onClick={fetchGalleries}
            className="flex items-center text-gray-600 hover:text-blue-600"
          >
            <RefreshCw size={18} className="mr-1" />
            {i18n.language === 'it' ? 'Aggiorna' : 'Refresh'}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Gallery List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden sticky top-4">
              <div className="p-4 border-b bg-gray-50">
                <h2 className="font-bold text-gray-900 mb-3">{i18n.language === 'it' ? 'Gallerie' : 'Galleries'}</h2>
                
                {/* Filter by type */}
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setFilterType('all')}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${filterType === 'all' ? 'bg-pink-600 text-white' : 'bg-gray-200 text-gray-600'}`}
                  >
                    {i18n.language === 'it' ? 'Tutte' : 'All'}
                  </button>
                  <button
                    onClick={() => setFilterType('events')}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${filterType === 'events' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}
                  >
                    {i18n.language === 'it' ? 'Eventi' : 'Events'}
                  </button>
                  <button
                    onClick={() => setFilterType('custom')}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${filterType === 'custom' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'}`}
                  >
                    {i18n.language === 'it' ? 'Personalizzate' : 'Custom'}
                  </button>
                </div>
                
                {/* Search */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={i18n.language === 'it' ? 'Cerca galleria...' : 'Search gallery...'}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                  />
                </div>

                {/* Show Archived Toggle */}
                <button
                  onClick={() => setShowArchived(!showArchived)}
                  className={`flex items-center text-sm w-full px-3 py-2 rounded-lg ${
                    showArchived ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <Archive size={16} className="mr-2" />
                  {showArchived ? (i18n.language === 'it' ? 'Mostra tutte' : 'Show all') : (i18n.language === 'it' ? 'Mostra archiviate' : 'Show archived')}
                </button>
              </div>

              {/* Gallery List */}
              <div className="max-h-[60vh] overflow-y-auto">
                {loading ? (
                  <div className="p-4 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : filteredGalleries.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {i18n.language === 'it' ? 'Nessuna galleria trovata' : 'No galleries found'}
                  </div>
                ) : (
                  filteredGalleries.map(gallery => (
                    <div
                      key={gallery.id}
                      className={`p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedGallery?.id === gallery.id ? 'bg-pink-50 border-l-4 border-l-pink-500' : ''
                      } ${gallery.gallery_visible === false ? 'opacity-60' : ''}`}
                      onClick={() => setSelectedGallery(gallery)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-grow min-w-0">
                          <div className="flex items-center gap-2">
                            {gallery.type === 'custom' ? (
                              <Folder size={14} className="text-purple-600 flex-shrink-0" />
                            ) : (
                              <Image size={14} className="text-blue-600 flex-shrink-0" />
                            )}
                            <p className="font-medium text-gray-900 truncate text-sm">
                              {gallery.name}
                            </p>
                          </div>
                          {gallery.type === 'event' && gallery.date_start && (
                            <p className="text-xs text-gray-500 mt-1 ml-5">
                              {gallery.date_start}
                            </p>
                          )}
                          {gallery.type === 'custom' && (
                            <p className="text-xs text-purple-500 mt-1 ml-5">
                              {i18n.language === 'it' ? 'Galleria personalizzata' : 'Custom gallery'}
                            </p>
                          )}
                          <div className="flex items-center gap-3 mt-1 ml-5 text-xs">
                            <span className="flex items-center text-pink-600">
                              <Image size={12} className="mr-1" />
                              {gallery.gallery_photos || 0}
                            </span>
                            <span className="flex items-center text-red-600">
                              <Video size={12} className="mr-1" />
                              {gallery.gallery_videos || 0}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {gallery.type === 'custom' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteCustomGallery(gallery);
                              }}
                              className="p-1.5 rounded text-red-500 hover:bg-red-50"
                              title={i18n.language === 'it' ? 'Elimina galleria' : 'Delete gallery'}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleVisibility(gallery);
                            }}
                            className={`p-1.5 rounded ${
                              gallery.gallery_visible !== false 
                                ? 'text-green-600 hover:bg-green-50' 
                                : 'text-gray-400 hover:bg-gray-100'
                            }`}
                            title={gallery.gallery_visible !== false ? (i18n.language === 'it' ? 'Visibile - clicca per archiviare' : 'Visible - click to archive') : (i18n.language === 'it' ? 'Archiviata - clicca per pubblicare' : 'Archived - click to publish')}
                          >
                            {gallery.gallery_visible !== false ? <Eye size={16} /> : <EyeOff size={16} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Gallery Management */}
          <div className="lg:col-span-2">
            {selectedGallery ? (
              <div className="space-y-6">
                {/* Gallery Header */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        {selectedGallery.type === 'custom' && (
                          <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">
                            {i18n.language === 'it' ? 'Personalizzata' : 'Custom'}
                          </span>
                        )}
                        <h2 className="text-2xl font-bold text-gray-900">{selectedGallery.name}</h2>
                      </div>
                      {selectedGallery.date_start && (
                        <p className="text-gray-500 mt-1">{selectedGallery.date_start}</p>
                      )}
                      {selectedGallery.description && (
                        <p className="text-gray-600 mt-2">{selectedGallery.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2">
                        <span className={`flex items-center px-2 py-1 rounded text-sm ${
                          selectedGallery.gallery_visible !== false 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {selectedGallery.gallery_visible !== false ? (
                            <><Eye size={14} className="mr-1" /> {i18n.language === 'it' ? 'Pubblica' : 'Published'}</>
                          ) : (
                            <><EyeOff size={14} className="mr-1" /> {i18n.language === 'it' ? 'Archiviata' : 'Archived'}</>
                          )}
                        </span>
                        <span className="text-sm text-gray-500">
                          {photos.length} foto • {videos.length} video
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggleVisibility(selectedGallery)}
                      className={`px-4 py-2 rounded-lg font-medium ${
                        selectedGallery.gallery_visible !== false
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {selectedGallery.gallery_visible !== false ? (i18n.language === 'it' ? 'Archivia' : 'Archive') : (i18n.language === 'it' ? 'Pubblica' : 'Publish')}
                    </button>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t">
                    <label className="flex items-center bg-pink-600 text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-pink-700 transition-colors">
                      <Upload size={18} className="mr-2" />
                      {i18n.language === 'it' ? 'Carica Foto' : 'Upload Photos'}
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                    <button
                      onClick={() => setShowVideoForm(!showVideoForm)}
                      className="flex items-center bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                    >
                      <Video size={18} className="mr-2" />
                      Aggiungi Video
                    </button>
                    {photos.length > 0 && (
                      <button
                        onClick={() => setSelectMode(!selectMode)}
                        className={`flex items-center px-4 py-2 rounded-lg ${
                          selectMode 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <CheckSquare size={18} className="mr-2" />
                        {selectMode ? 'Annulla selezione' : 'Seleziona'}
                      </button>
                    )}
                  </div>

                  {/* Upload Progress */}
                  {uploading && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                        <span>Caricamento...</span>
                        <span>{progress.done} / {progress.total}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-pink-600 h-2 rounded-full transition-all"
                          style={{ width: `${(progress.done / progress.total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Video Form */}
                  {showVideoForm && (
                    <form onSubmit={handleAddVideo} className="mt-4 bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-900 mb-3">Aggiungi Video YouTube</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            URL YouTube *
                          </label>
                          <input
                            type="url"
                            value={videoUrl}
                            onChange={(e) => setVideoUrl(e.target.value)}
                            placeholder="https://www.youtube.com/watch?v=..."
                            className="w-full px-3 py-2 border rounded-lg"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Titolo
                          </label>
                          <input
                            type="text"
                            value={videoTitle}
                            onChange={(e) => setVideoTitle(e.target.value)}
                            placeholder="Es: Highlights 2024"
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button type="submit" className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">
                          Aggiungi
                        </button>
                        <button type="button" onClick={() => setShowVideoForm(false)} className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg">
                          Annulla
                        </button>
                      </div>
                    </form>
                  )}

                  {error && (
                    <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg">
                      {error}
                    </div>
                  )}
                </div>

                {/* Selection Actions */}
                {selectMode && selectedItems.size > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
                    <span className="font-medium text-blue-800">
                      {selectedItems.size} elementi selezionati
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={selectAllPhotos}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
                      >
                        {selectedItems.size === photos.length ? 'Deseleziona tutto' : 'Seleziona tutto'}
                      </button>
                      <button
                        onClick={handleDeleteSelected}
                        className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm flex items-center"
                      >
                        <Trash2 size={14} className="mr-1" />
                        Elimina selezionati
                      </button>
                    </div>
                  </div>
                )}

                {/* Photos Grid */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Image size={20} className="mr-2 text-pink-600" />
                    Foto ({photos.length})
                  </h3>
                  
                  {photos.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <Image size={48} className="mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">{i18n.language === 'it' ? 'Nessuna foto. Carica Foto per aggiungerne.' : 'No photos. Upload Photos to add some.'}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                      {photos.map((item) => (
                        <div 
                          key={item.id} 
                          className={`relative group aspect-square rounded-lg overflow-hidden ${
                            selectMode ? 'cursor-pointer' : ''
                          } ${selectedItems.has(item.id) ? 'ring-4 ring-blue-500' : ''}`}
                          onClick={() => selectMode ? toggleItemSelection(item.id) : setPreviewImage(item)}
                        >
                          <img
                            src={getPhotoUrl(item)}
                            alt={item.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          
                          {/* Selection checkbox */}
                          {selectMode && (
                            <div className="absolute top-2 left-2">
                              {selectedItems.has(item.id) ? (
                                <CheckSquare className="text-blue-500 bg-white rounded" size={20} />
                              ) : (
                                <Square className="text-white bg-black/30 rounded" size={20} />
                              )}
                            </div>
                          )}
                          
                          {/* Delete button (only when not in select mode) */}
                          {!selectMode && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(item.id);
                              }}
                              className="absolute top-2 right-2 bg-red-600 text-white p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Videos Grid */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Video size={20} className="mr-2 text-red-600" />
                    Video ({videos.length})
                  </h3>
                  
                  {videos.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <Video size={48} className="mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">{i18n.language === 'it' ? 'Nessun video. Aggiungi Video per inserire un link YouTube.' : 'No videos. Add Video to insert a YouTube link.'}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {videos.map((item) => (
                        <div key={item.id} className="relative group">
                          <a href={item.video_url} target="_blank" rel="noopener noreferrer" className="block">
                            <div className="relative aspect-video rounded-lg overflow-hidden">
                              <img
                                src={item.image_url}
                                alt={item.title}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50">
                                <Play className="text-white" size={40} />
                              </div>
                            </div>
                            <p className="mt-2 text-sm font-medium text-gray-900 truncate">{item.title}</p>
                          </a>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="absolute top-2 right-2 bg-red-600 text-white p-1.5 rounded opacity-0 group-hover:opacity-100"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <Image size={64} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  {i18n.language === 'it' ? 'Seleziona una galleria' : 'Select a gallery'}
                </h3>
                <p className="text-gray-500">
                  {i18n.language === 'it' ? 'Scegli una galleria dalla lista o creane una nuova.' : 'Choose a gallery from the list or create a new one.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <X size={32} />
          </button>
          <img
            src={getPhotoUrl(previewImage)}
            alt={previewImage.title}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Create Custom Gallery Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                {i18n.language === 'it' ? 'Nuova Galleria Personalizzata' : 'New Custom Gallery'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewGalleryName('');
                  setNewGalleryDescription('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleCreateCustomGallery}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {i18n.language === 'it' ? 'Nome Galleria' : 'Gallery Name'} *
                </label>
                <input
                  type="text"
                  value={newGalleryName}
                  onChange={(e) => setNewGalleryName(e.target.value)}
                  placeholder={i18n.language === 'it' ? 'Es: Facce da Offroad Italia' : 'E.g.: Offroad Italia Faces'}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  required
                  autoFocus
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {i18n.language === 'it' ? 'Descrizione (opzionale)' : 'Description (optional)'}
                </label>
                <textarea
                  value={newGalleryDescription}
                  onChange={(e) => setNewGalleryDescription(e.target.value)}
                  placeholder={i18n.language === 'it' ? 'Descrizione della galleria...' : 'Gallery description...'}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  rows={3}
                />
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewGalleryName('');
                    setNewGalleryDescription('');
                    setError('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {i18n.language === 'it' ? 'Annulla' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 font-medium"
                >
                  {i18n.language === 'it' ? 'Crea Galleria' : 'Create Gallery'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGalleries;
